import {
  loadCheckoutWebComponents,
  type CheckoutError,
  type CheckoutWebComponents,
  type Component,
  type Options,
  type PayPaymentSessionSuccessfulResponse,
} from '@checkout.com/checkout-web-components'
import { PaymentServiceType } from '@dni-types/data-models/ecommerce-payment'
import { type ValueOf } from '@dni-types/utilities/value-of'
import { type PaymentMethod } from '@hooks-api/types/dataModel/payment'
import { PAYMENT_ERROR_CODE, PaymentError } from '../PaymentError'
import { type CheckoutPayFlowModule, type FlowPaymentSetting } from '../types'
import { type PaymentDialogType } from './payment-dialog-type'

const DEFAULT_LOCALE = 'zh-TW'

/** Site locale (underscore) → CKO Flow SDK locale (hyphen). Unrecognized locales fall back to DEFAULT_LOCALE. */
const LOCALE_MAP: Partial<Record<string, string>> = {
  zh_TW: 'zh-TW',
  zh_HK: 'zh-HK',
  en_US: 'en',
}

export const mapToCkoLocale = (siteLocale?: string): string => {
  if (!siteLocale) return DEFAULT_LOCALE
  return LOCALE_MAP[siteLocale] ?? DEFAULT_LOCALE
}

type CompletedCallback = (component: Component, payment: PayPaymentSessionSuccessfulResponse) => void
type ErrorCallback = (component: Component, error: CheckoutError) => void

export const PAYMENT_EXECUTION_MODE = {
  /** 信用卡 / Apple Pay：SDK callback 完成 */
  INLINE: 'inline',
  /** CKO Flow SDK submit() 觸發整頁跳轉（Alipay / WeChat） */
  CKO_REDIRECT: 'cko_redirect',
} as const

export type PaymentExecutionMode = ValueOf<typeof PAYMENT_EXECUTION_MODE>

/** 次要 mount 目標的覆寫；未提供時由 module 自行從 payment method 推導。 */
export interface MountTargetOverrides {
  storedCardContainerId?: string
}

export abstract class BaseCheckoutPayFlowModule implements CheckoutPayFlowModule<FlowPaymentSetting> {
  protected appEnv: FlowPaymentSetting['appEnv'] = 'sandbox'
  protected appKey = ''
  protected locale: string = DEFAULT_LOCALE
  protected checkout: CheckoutWebComponents | null = null
  protected component: Component | null = null
  protected paymentSession: Options['paymentSession'] | null = null
  protected componentOptions: Options['componentOptions'] | null = null

  /** Distinguishes "never mounted" from "mount() ran but isAvailable() returned false"
   *  for submit() error messages. */
  private mountAttempted = false

  private mountPromise: Promise<void> | null = null

  protected mountedContainerSelector: string | null = null

  /** 記錄 node 本身而非只記 selector 字串，避免舊 node 已移除卻誤判掛載仍有效。 */
  private mountedContainerElement: Element | null = null

  private onPaymentCompletedCallback: CompletedCallback | null = null
  private onErrorCallback: ErrorCallback | null = null

  /**
   * Creates and returns the SDK component for this payment method.
   * Subclasses implement this to call `checkout.create('card')`, `checkout.create('applepay')`, etc.
   * The callbacks are already wired — pass them directly into the create options.
   */
  protected abstract createComponent(
    checkout: CheckoutWebComponents,
    callbacks: { onPaymentCompleted: CompletedCallback; onError: ErrorCallback },
  ): Component

  public loadScript(): Promise<void> {
    return Promise.resolve()
  }

  public getPaymentExecutionMode(): PaymentExecutionMode {
    return PAYMENT_EXECUTION_MODE.INLINE
  }

  // 送出訂單後才在彈窗內 mount（Apple Pay、WeChat QR），而非掛在付款列表上。
  public mountsInDialog(): boolean {
    return false
  }

  public getPaymentDialogType(): PaymentDialogType | null {
    return null
  }

  /** 回 true 會跳過 re-init、保留目前 component，但也放棄套用新的 payment session，條件須綁在「使用者真的填了東西」上。 */
  public shouldPreserveMountedState(): boolean {
    return false
  }

  /**
   * Hook called by the wiring layer before `mount()`. Subclasses can override to
   * derive additional configuration from the resolved payment method (e.g. setting
   * secondary mount targets for composite components). Default is no-op.
   *
   * `overrides` 優先於 method 推導；取捨由 subclass 做完，呼叫端不要事後再覆寫一次。
   */
  public configureBeforeMount(_method: PaymentMethod, _overrides?: MountTargetOverrides): void {
    // no-op
  }

  public configurePayment(details: FlowPaymentSetting): void {
    const newLocale = mapToCkoLocale(details.locale)
    const sameConfig = (
      details.appKey === this.appKey
      && details.appKey !== ''
      && details.appEnv === this.appEnv
      && newLocale === this.locale
    )
    if (sameConfig && !details.shouldReinitialize) return

    this.appEnv = details.appEnv
    this.appKey = details.appKey ?? ''
    this.locale = newLocale
  }

  public setPaymentSession(session: Options['paymentSession']): void {
    const oldId = this.getPaymentSessionId()
    const newId = typeof session?.id === 'string' ? session.id : null
    if (oldId !== newId) {
      // session 換了 → cached checkout instance 綁的是舊 session，必須 reload 才會真的吃到新 token
      // 也清 mountPromise，否則 mount() dedup 會回傳上一個 session 的 in-flight promise
      this.unmount()
      this.checkout = null
      this.mountPromise = null
    }
    this.paymentSession = session
  }

  public getPaymentSessionId(): string | null {
    const id = this.paymentSession?.id
    return typeof id === 'string' ? id : null
  }

  public getAppEnv(): FlowPaymentSetting['appEnv'] {
    return this.appEnv
  }

  public setComponentOptions(options: Options['componentOptions']): void {
    this.componentOptions = options ?? null
  }

  public onPaymentCompleted(callback: CompletedCallback | null): void {
    this.onPaymentCompletedCallback = callback
  }

  public onError(callback: ErrorCallback | null): void {
    this.onErrorCallback = callback
  }

  /**
   * Build callback wrappers that delegate to the module's current setter targets.
   * Lets a subclass register multiple SDK components against a single shared
   * `onPaymentCompleted` / `onError` listener.
   */
  protected createCallbackWrappers(): { onPaymentCompleted: CompletedCallback; onError: ErrorCallback } {
    return {
      onPaymentCompleted: (component, payment) => this.onPaymentCompletedCallback?.(component, payment),
      onError: (component, error) => {
        console.debug('[CKO Flow] onError', { error, code: error?.code, isValid: component.isValid?.(), selectedType: component.selectedType })
        this.onErrorCallback?.(component, error)
      },
    }
  }

  /**
   * Initialize the SDK without mounting any DOM component. Subclasses use this when
   * they need the `checkout` instance to create components conditionally before deciding
   * which one to mount.
   */
  protected async bootSdk(
    appKey: string,
    paymentSession: Options['paymentSession'] | null,
    locale: string,
    componentOptions: Options['componentOptions'] | null,
  ): Promise<void> {
    if (this.checkout) return

    if (!paymentSession) {
      throw new PaymentError(
        '[BaseCheckoutPayFlowModule] paymentSession is required before mount',
        PaymentServiceType.CHECKOUTPAY,
        PAYMENT_ERROR_CODE.CONFIGURATION_FAILED,
      )
    }

    if (!appKey) {
      throw new PaymentError(
        '[BaseCheckoutPayFlowModule] appKey is required before mount',
        PaymentServiceType.CHECKOUTPAY,
        PAYMENT_ERROR_CODE.CONFIGURATION_FAILED,
      )
    }

    const environment = appKey.startsWith('pk_sbox_') ? 'sandbox' : 'production'

    const options: Options = {
      publicKey: appKey,
      environment,
      locale,
      paymentSession,
      ...(componentOptions != null && { componentOptions }),
    }

    try {
      this.checkout = await loadCheckoutWebComponents(options)
      console.info(
        `[CKO Flow] ${this.constructor.name} SDK initialized: environment=${options.environment} appEnv=${this.appEnv}`,
      )
    }
    catch (error) {
      throw new PaymentError(
        `[BaseCheckoutPayFlowModule] SDK initialization failed: ${error}`,
        PaymentServiceType.CHECKOUTPAY,
        PAYMENT_ERROR_CODE.CONFIGURATION_FAILED,
        error,
      )
    }
  }

  public mount(element: string | Element): Promise<void> {
    if (this.hasMountedComponent()) return Promise.resolve()
    if (this.mountPromise) return this.mountPromise

    const inflight = this.mountInternal(element).finally(() => {
      if (this.mountPromise === inflight) this.mountPromise = null
    })
    this.mountPromise = inflight
    return inflight
  }

  protected async mountInternal(element: string | Element): Promise<void> {
    const { appKey, paymentSession, locale, componentOptions } = this
    await this.bootSdk(appKey, paymentSession, locale, componentOptions)
    this.mountAttempted = true

    if (!this.checkout) {
      throw new PaymentError(
        '[BaseCheckoutPayFlowModule] SDK not initialized after bootSdk',
        PaymentServiceType.CHECKOUTPAY,
        PAYMENT_ERROR_CODE.CONFIGURATION_FAILED,
      )
    }

    this.component = this.createComponent(this.checkout, this.createCallbackWrappers())

    const isAvailable = await this.component.isAvailable()
    if (!isAvailable) {
      // 清掉讓 retry mount 不被 idempotency 擋住；component 只 create 過沒真的掛 DOM，不需 unmount
      this.component = null
      this.checkout = null
      throw new PaymentError(
        '[BaseCheckoutPayFlowModule] component.isAvailable() returned false',
        PaymentServiceType.CHECKOUTPAY,
        PAYMENT_ERROR_CODE.CONFIGURATION_FAILED,
      )
    }

    // bootSdk()/isAvailable() 期間頁面可能已把 mount container 卸載（切換付款方式、頁面導轉），
    // 此時仍呼叫 SDK mount() 會丟 element_not_found；改成靜默跳過，交由下次 mount() 重試。
    if (!this.isMountContainerInDom(element)) {
      console.warn('[BaseCheckoutPayFlowModule] mount container no longer in DOM — skipping mount')
      this.component = null
      this.checkout = null
      return
    }

    this.component.mount(element)
    this.recordMountTarget(element)
  }

  protected isMountContainerInDom(element: string | Element): boolean {
    if (typeof element === 'string') return document.querySelector(element) !== null
    return element.isConnected
  }

  /** 只在 SDK 真的掛上去之後才記錄，避免記到「以為掛了、實際沒掛成功」的目標。 */
  protected recordMountTarget(element: string | Element): void {
    this.mountedContainerSelector = typeof element === 'string' ? element : null
    this.mountedContainerElement = typeof element === 'string' ? document.querySelector(element) : element
  }

  /** Subclasses extend to include alternative components (e.g. stored_card). */
  protected hasMountedComponent(): boolean {
    return this.component !== null
  }

  public isMounted(): boolean {
    return this.hasMountedComponent() && this.mountPromise === null && this.isMountedContainerConnected()
  }

  /** module 為 singleton，持有 component 不代表它仍顯示在畫面上，因此改以掛載當時記錄的 node 是否仍在頁面中判定。 */
  public isMountedContainerConnected(): boolean {
    return this.mountedContainerElement?.isConnected ?? false
  }

  /** `isMounted()` 對「掛在別處」也回 true，在意掛載目標的呼叫端必須比對這個值。 */
  public getMountedContainerElement(): Element | null {
    return this.mountedContainerElement
  }

  public getMountedContainerSelector(): string | null {
    return this.mountedContainerSelector
  }

  /** mount() 是否進行中；供呼叫端在同 session 雙重觸發時等待現有 mount 完成、避免並跑競速。 */
  public isMounting(): boolean {
    return this.mountPromise !== null
  }

  /** Lets mount errors propagate — if mount() failed, submit() should surface
   *  that, not a misleading "component not available" downstream. */
  public awaitMount(): Promise<void> {
    return this.mountPromise ?? Promise.resolve()
  }

  public unmount(): void {
    this.component?.unmount()
    this.component = null
    this.mountedContainerSelector = null
    this.mountedContainerElement = null
    // 一併清 mountPromise：否則同 session 下一次 mount() 會被 dedup 回傳舊容器的 in-flight promise，新容器掛不上。
    this.mountPromise = null
  }

  /**
   * Fire-and-forget; payment result is delivered through onPaymentCompleted / onError
   * callbacks, not from this return value.
   *
   * @throws PaymentError if called before mount, or if a prior mount() reset the
   *   component because the SDK reported it unavailable.
   */
  public async submit(): Promise<void> {
    await this.awaitMount()
    if (!this.component) {
      const reason = this.mountAttempted
        ? 'submit() called when component is not available — did mount() succeed?'
        : 'submit() called before mount'
      throw new PaymentError(
        `[BaseCheckoutPayFlowModule] ${reason}`,
        PaymentServiceType.CHECKOUTPAY,
        PAYMENT_ERROR_CODE.CONFIGURATION_FAILED,
      )
    }
    this.component.submit()
  }

  public reset(): void {
    this.unmount()
    this.checkout = null
    this.paymentSession = null
    this.componentOptions = null
    this.appKey = ''
    this.appEnv = 'sandbox'
    this.locale = DEFAULT_LOCALE
    this.mountAttempted = false
    this.mountPromise = null
    this.onPaymentCompletedCallback = null
    this.onErrorCallback = null
  }
}
